import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { ChatMessage, ChatMember, ReadState, MessageContent } from '@/api/c2c/types'
import { chatInitApi, chatTicketApi } from '@/api'
import type { EventBusKey } from '@vueuse/core'
import { useEventBus } from '@vueuse/core'
import { BizKind, UpEvent, DownEvent, OrderNotifyKind } from '@/enums/chat'
import { useNotifyStore } from './notify'
import { useSessionStore } from './session'
export const CHAT_PUSH_EVENT: EventBusKey<string> = Symbol('chat-push-event')

export const chatBus = useEventBus<string>(CHAT_PUSH_EVENT)

/** WebSocket 消息事件类型 */
type WsEvent = UpEvent

/** WebSocket 业务类型 */
type WsBiz = BizKind

/** WebSocket 发送消息格式 */
interface WsMessage<T = any> {
  biz: WsBiz
  event: WsEvent
  client_ts: number
  payload: T
}

/** 心跳 payload */
interface HeartbeatPayload {
  client_ts: number
}

/** 加入/离开聊天 payload */
interface ChatJoinLeavePayload {
  session_id: string
}

/** 发送消息 payload */
interface MsgSendPayload {
  client_msg_id: string
  session_id: string
  content: { type: string; data: any }[]
}

/** 读取消息 payload */
interface MsgReadPayload {
  last_msg_id: number
  session_id: string
}

/** 通知 feed 数据结构 */
interface NotifyFeed {
  order_no: string
  /** 订单状态枚举，需做状态+显示文字的映射 */
  order_status: number
  currency: string
  amount: string
  /** 过期绝对时间(秒) */
  expires_at: number
  /** 服务器时间(秒) */
  server_now: number
}

/** 通知基础 payload */
interface BaseNotifyPayload {
  kind: OrderNotifyKind
  msg_id: number
  /** 是否给通知中心未读计数+1 */
  center_incr: boolean
  /** 是否给订单 TAB 未读计数+1 */
  order_tab_incr: boolean
  sent_at: number
  feed: NotifyFeed
}

/** 订单状态更新通知 payload（悬浮框通知） */
interface OrderStatusUpdatePayload extends BaseNotifyPayload {
  /** 订单需操作通知，用于解析"feed" */
  kind: OrderNotifyKind
}

/** 首页中间通知 payload */
interface MiddleNotifyPayload extends BaseNotifyPayload {
  /** 订单更新，用于解析"feed" */
  kind: OrderNotifyKind
}

/** WebSocket 接收到的消息 */
interface WsReceivedMessage {
  biz: WsBiz
  event: string
  payload: any
}

/**
 * 聊天 Store
 */
export const useChatStore = defineStore('chat', () => {
  /** 通知 store */
  const notifyStore = useNotifyStore()

  /** WebSocket 实例 */
  const ws = ref<WebSocket | null>(null)

  /** 连接状态 */
  const isConnected = ref(false)

  /** 当前会话 ID */
  const currentSessionId = ref<string>('')

  /** WebSocket 连接票据 */
  const ticket = ref<string>('')

  /** 聊天成员列表 */
  const members = ref<ChatMember[]>([])

  /** 消息列表 */
  const messages = ref<ChatMessage[]>([])

  /** 已读状态 */
  const readState = ref<ReadState[]>([])

  /** 心跳定时器 */
  const heartbeatTimer = ref<number | null>(null)

  /** 心跳 ACK 超时定时器 */
  const heartbeatAckTimer = ref<number | null>(null)

  /** 页面隐藏时间戳（ms） */
  const hiddenAt = ref<number | null>(null)

  /** visibilitychange 监听函数引用（用于注销） */
  const visibilityHandler = ref<(() => void) | null>(null)

  /** 通知计数 */
  const notifyCount = ref(0)

  /** 订单 tab 角标计数 */
  const orderTabCount = ref(0)

  /**
   * 连接 WebSocket（全局连接，登录后调用）
   * @returns 连接成功 true；无 session、连接失败 false（ticket 请求失败仍会抛错）
   */
  async function connectWebSocket(): Promise<boolean> {
    const sessionStore = useSessionStore()
    if (!sessionStore.sessionId) return false
    // 如果已连接，先关闭
    if (ws.value) {
      console.warn('WebSocket 已连接，先关闭旧连接')
      closeWebSocket()
    }

    // 获取 ticket
    const ticketRes = await chatTicketApi()
    ticket.value = ticketRes.data.ticket

    return initWebSocket(ticket.value)
  }

  /**
   * 初始化聊天室（进入聊天室时调用）
   */
  async function initChat(session_id: string) {
    currentSessionId.value = session_id

    // 获取聊天室初始数据
    const initRes = await chatInitApi({ session_id: currentSessionId.value, size: 50 })

    // 保存聊天初始化数据
    setChatData({
      sessionId: initRes.data.session_id,
      members: initRes.data.members,
      messages: initRes.data.list,
      readState: initRes.data.read_state,
    })

    // 如果 WebSocket 已连接，加入聊天室
    if (isConnected.value) {
      joinChat(currentSessionId.value)
    } else {
      console.error('WebSocket 未连接，请先调用 connectWebSocket()')
      connectWebSocket()
        .then((ok) => {
          if (ok && currentSessionId.value) {
            joinChat(currentSessionId.value)
          }
        })
        .catch((e) => console.error('重连失败:', e))
    }
  }

  /**
   * 初始化 WebSocket 连接
   * @param ticket WebSocket 连接票据
   * @returns 握手成功 resolve(true)，失败或关闭在未完成握手前 resolve(false)；已存在连接时 resolve(true)
   */
  function initWebSocket(ticket: string): Promise<boolean> {
    if (ws.value) {
      console.warn('WebSocket 已连接')
      return Promise.resolve(true)
    }

    return new Promise((resolve) => {
      let settled = false
      const finish = (ok: boolean) => {
        if (settled) return
        settled = true
        resolve(ok)
      }

      const wsUrl = `wss://${import.meta.env.VITE_API_WS_URL || location.host}/ws/?ticket=${ticket}`
      const socket = new WebSocket(wsUrl)
      ws.value = socket

      socket.onopen = () => {
        console.log('WebSocket 连接成功')
        isConnected.value = true
        startHeartbeat()

        // 如果已有当前会话，自动加入
        if (currentSessionId.value) {
          joinChat(currentSessionId.value)
        }
        finish(true)
      }

      socket.onmessage = (e) => {
        try {
          const data: WsReceivedMessage = JSON.parse(e.data)
          handleMessage(data)
        } catch (error) {
          console.error('WebSocket 消息解析失败:', error)
        }
      }

      socket.onerror = (e) => {
        console.error('WebSocket 错误:', e)
        finish(false)
      }

      socket.onclose = (e) => {
        console.log('WebSocket 关闭:', e.code, e.reason)
        isConnected.value = false
        stopHeartbeat()
        finish(false)
        if (ws.value === socket) {
          ws.value = null
        }
      }

      chatBus.emit('WebSocket initialized')
    })
  }

  /**
   * 关闭 WebSocket 连接
   */
  function closeWebSocket() {
    if (ws.value) {
      stopHeartbeat()
      ws.value.close()
      ws.value = null
      isConnected.value = false
    }
  }

  /**
   * 发送 WebSocket 消息
   */
  function sendMessage<T>(biz: WsBiz, event: WsEvent, payload: T) {
    if (!ws.value || !isConnected.value) {
      console.error('WebSocket 未连接')
      return
    }

    const message: WsMessage<T> = {
      biz,
      event,
      client_ts: Date.now(),
      payload,
    }

    ws.value.send(JSON.stringify(message))
  }

  /**
   * 处理接收到的消息
   */
  function handleMessage(data: WsReceivedMessage) {
    console.log('收到消息:', data)

    switch (data.event) {
      case UpEvent.Heartbeat:
      case DownEvent.HeartbeatAck:
        /** 心跳响应，清除 ACK 超时 */
        clearHeartbeatAckTimer()
        break
      /** 收到订单状态变更消息 */
      case DownEvent.OrderStatusChanged:
        chatBus.emit(DownEvent.OrderStatusChanged, data.payload)
        break
      /** 收到消息推送 */
      case DownEvent.MsgPush:
        /** 收到新消息 */
        messages.value.push(data.payload)
        chatBus.emit(data.payload)
        break
      case DownEvent.MsgReadPush:
        /** 对方已读消息 */
        updateReadState(data.payload)
        break
      case DownEvent.NotifyPopup:
        /** 悬浮框通知 */
        handleOrderStatusUpdate(data.payload)
        break
      case DownEvent.NotifyMiddle:
        /** 首页中间通知 */
        handleMiddleNotify(data.payload)
        break
      case DownEvent.NotifyPopupRead:
        /** 服务端通知某些悬浮弹窗已读，批量移除 */
        ;(data.payload.message_ids as number[]).forEach((id) =>
          notifyStore.removeOrderNotification(id),
        )
        break
      case DownEvent.ChatOrderStatusUpdate:
        /** 聊天室订单状态更新 */
        chatBus.emit(DownEvent.ChatOrderStatusUpdate, data.payload)
        break
      case DownEvent.PeerOnlinePush:
        /** 聊天室成员在线状态更新 */
        chatBus.emit(DownEvent.PeerOnlinePush, data.payload)
        break
      default:
        console.log('未处理的事件:', data.event)
    }
  }

  /**
   * 启动心跳
   */
  function startHeartbeat() {
    stopHeartbeat()

    heartbeatTimer.value = window.setInterval(() => {
      sendHeartbeat()
    }, 20 * 1000) /** 每 20 秒发送一次心跳 */

    // 注册页面可见性监听：回到页面时若离开超过 20 秒，立即发一次心跳
    const handler = () => {
      // console.log('页面可见性变化:', document.visibilityState)
      if (document.visibilityState === 'hidden') {
        hiddenAt.value = Date.now()
      } else if (document.visibilityState === 'visible') {
        if (!isConnected.value || ws.value === null || ws.value.readyState !== WebSocket.OPEN) {
          // 连接已关闭（如锁屏导致），重新连接
          console.warn('页面重新可见，检测到 WebSocket 已断开，开始重连...')
          const sessionIdToRejoin = currentSessionId.value
          closeWebSocket()
          connectWebSocket()
            .then((ok) => {
              if (ok && sessionIdToRejoin) {
                joinChat(sessionIdToRejoin)
              }
            })
            .catch((e) => console.error('重连失败:', e))
        } else if (hiddenAt.value !== null && Date.now() - hiddenAt.value > 20 * 1000) {
          // 连接仍然存活，但离开超过 20 秒，立即补发一次心跳
          sendHeartbeat()
        }
        hiddenAt.value = null
      }
    }
    if (visibilityHandler.value) {
      document.removeEventListener('visibilitychange', visibilityHandler.value)
      visibilityHandler.value = null
    }
    visibilityHandler.value = handler
    document.addEventListener('visibilitychange', handler)
  }

  /**
   * 停止心跳
   */
  function stopHeartbeat() {
    if (heartbeatTimer.value) {
      clearInterval(heartbeatTimer.value)
      heartbeatTimer.value = null
    }
    clearHeartbeatAckTimer()
    // if (visibilityHandler.value) {
    //   document.removeEventListener('visibilitychange', visibilityHandler.value)
    //   visibilityHandler.value = null
    // }
    hiddenAt.value = null
  }

  /**
   * 清除心跳 ACK 超时定时器
   */
  function clearHeartbeatAckTimer() {
    if (heartbeatAckTimer.value) {
      clearTimeout(heartbeatAckTimer.value)
      heartbeatAckTimer.value = null
    }
  }

  /**
   * 发送心跳
   */
  function sendHeartbeat() {
    const payload: HeartbeatPayload = {
      client_ts: Date.now(),
    }
    sendMessage(BizKind.System, UpEvent.Heartbeat, payload)

    // 启动 ACK 超时：5 秒内未收到 HeartbeatAck 则重连
    clearHeartbeatAckTimer()
    heartbeatAckTimer.value = window.setTimeout(async () => {
      console.warn('心跳超时，未收到 HeartbeatAck，开始重连...')
      const sessionIdToRejoin = currentSessionId.value
      closeWebSocket()
      try {
        await connectWebSocket()
        if (sessionIdToRejoin) {
          joinChat(sessionIdToRejoin)
        }
      } catch (e) {
        console.error('重连失败:', e)
      }
    }, 5 * 1000)
  }

  /**
   * 加入聊天室
   */
  function joinChat(sessionId: string) {
    currentSessionId.value = sessionId
    const payload: ChatJoinLeavePayload = {
      session_id: sessionId,
    }
    sendMessage(BizKind.Chat, UpEvent.OrderChatJoin, payload)
  }

  /**
   * 离开聊天室
   */
  function leaveChat() {
    if (!currentSessionId.value) return

    const payload: ChatJoinLeavePayload = {
      session_id: currentSessionId.value,
    }
    sendMessage(BizKind.Chat, UpEvent.OrderChatLeave, payload)
    currentSessionId.value = ''
  }

  /**
   * 发送聊天消息
   * @param text 消息文本
   * @param images 图片文件 file_id
   */
  function sendChatMessage(text: string, img?: number) {
    if (!currentSessionId.value) {
      console.error('未加入聊天室')
      return
    }
    const msgContent = []
    if (text) {
      msgContent.push({ type: 'text', data: text })
    }
    if (img) {
      msgContent.push({ type: 'image', data: img })
    }
    const clientMsgId = `cmsg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
    const payload: MsgSendPayload = {
      client_msg_id: clientMsgId,
      session_id: currentSessionId.value,
      content: msgContent,
    }
    sendMessage(BizKind.Chat, UpEvent.MsgSend, payload)
  }

  /**
   * 标记消息已读
   */
  function markMessagesAsRead(lastMsgId: number) {
    if (!currentSessionId.value) return

    const payload: MsgReadPayload = {
      last_msg_id: lastMsgId,
      session_id: currentSessionId.value,
    }
    sendMessage(BizKind.Chat, UpEvent.MsgRead, payload)
  }

  /**
   * 更新已读状态
   */
  function updateReadState(newState: ReadState | ReadState[]) {
    const states = Array.isArray(newState) ? newState : [newState]

    states.forEach((state) => {
      const index = readState.value.findIndex((s) => s.member_id === state.member_id)
      if (index !== -1) {
        readState.value[index] = state
      } else {
        readState.value.push(state)
      }
    })
  }

  /**
   * 处理订单状态更新通知（悬浮框通知）
   */
  function handleOrderStatusUpdate(payload: OrderStatusUpdatePayload) {
    console.log('悬浮框通知:', payload)

    // 根据服务端标识判断是否增加通知中心计数
    if (payload.center_incr) {
      notifyCount.value++
      notifyStore.centerUnread++
    }

    // 根据服务端标识判断是否增加订单 tab 角标计数
    if (payload.order_tab_incr) {
      orderTabCount.value++
      notifyStore.tabOrderUnread++
    }

    // 添加到悬浮通知队列
    notifyStore.addOrderNotification(payload)
  }

  /**
   * 处理首页中间通知
   */
  function handleMiddleNotify(payload: MiddleNotifyPayload) {
    console.log('首页中间通知:', payload)

    // 根据服务端标识判断是否增加通知中心计数
    if (payload.center_incr) {
      notifyCount.value++
    }

    // 根据服务端标识判断是否增加订单 tab 角标计数
    if (payload.order_tab_incr) {
      orderTabCount.value++
    }

    // 发送事件供组件监听（用于首页中间通知显示）
    chatBus.emit(DownEvent.NotifyMiddle, payload)
  }

  /**
   * 清零通知计数
   */
  function clearNotifyCount() {
    notifyCount.value = 0
  }

  /**
   * 清零订单 tab 角标计数
   */
  function clearOrderTabCount() {
    orderTabCount.value = 0
  }

  /**
   * 设置聊天初始数据
   */
  function setChatData(data: {
    sessionId: string
    members: ChatMember[]
    messages: ChatMessage[]
    readState: ReadState[]
  }) {
    currentSessionId.value = data.sessionId
    members.value = data.members
    messages.value = data.messages
    readState.value = data.readState
  }

  /**
   * 添加历史消息（用于加载更多）
   */
  function prependMessages(newMessages: ChatMessage[]) {
    messages.value = [...newMessages, ...messages.value]
  }

  /**
   * 清空聊天数据
   */
  function clearChatData() {
    currentSessionId.value = ''
    members.value = []
    messages.value = []
    readState.value = []
  }

  /**
   * 获取对方成员信息（假设只有两个成员）
   */
  const otherMember = computed(() => {
    /** TODO: 根据当前用户 ID 过滤 */
    return members.value.find((m) => m.id !== 0) /** 临时逻辑 */
  })

  return {
    // 状态
    ws,
    isConnected,
    currentSessionId,
    members,
    messages,
    readState,
    otherMember,
    notifyCount,
    orderTabCount,

    // 方法
    connectWebSocket,
    initChat,
    closeWebSocket,
    joinChat,
    leaveChat,
    sendChatMessage,
    markMessagesAsRead,
    setChatData,
    prependMessages,
    clearChatData,
    clearNotifyCount,
    clearOrderTabCount,
  }
})
